import commonMessages from '@rhg/lang/messages/common';
import { Box, CircularProgress } from '@rhg/libs/material';
import { forwardRef } from 'react';
import { useIntl } from 'react-intl';

// Matches the default `CircularProgress` size so the row keeps its height
// while the spinner is hidden.
const SPINNER_SIZE = 40;

// Full-width row inside any CSS grid (works for repeat(n,1fr), auto-fill, and
// fixed-column grids). Must be a direct grid child and NOT wrapped in a
// GlobalGridItem, which would clamp it to a column span.
const styleSentinel = {
    gridColumn: '1 / -1',
    display: 'flex',
    justifyContent: 'center',
    paddingY: 4,
};

const styleSpinnerSlot = {
    width: SPINNER_SIZE,
    height: SPINNER_SIZE,
};

interface PaginationSentinelProps {
    /** Show the spinner. Pass the `useClientPagination` `isRevealing` flag. */
    isLoading: boolean;
}

/**
 * Sentinel rendered after the visible items in a client-paginated grid.
 * Attach the `useClientPagination` `sentinelRef` to it and only render it while
 * there are more items to reveal; it disappears (and disconnects its observer)
 * once the list is exhausted. The spinner only shows while a batch is being
 * revealed, so an off-screen sentinel is not an indeterminate loader.
 */
export const PaginationSentinel = forwardRef<
    HTMLDivElement,
    PaginationSentinelProps
>(function PaginationSentinel({ isLoading }, ref) {
    const { formatMessage } = useIntl();
    return (
        // Stays mounted while the spinner is hidden: a live region only
        // announces content added to a region already in the DOM.
        <Box ref={ref} sx={styleSentinel} role="status" aria-live="polite">
            <Box sx={styleSpinnerSlot}>
                {isLoading && (
                    <CircularProgress
                        color="secondary"
                        aria-label={formatMessage(
                            commonMessages['common.loading']
                        )}
                    />
                )}
            </Box>
        </Box>
    );
});
